import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Pill, Heart, Activity, Moon, Utensils, Smile, AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { ComprehensiveUserProfile } from './types';

interface HealthTabProps {
  profile: ComprehensiveUserProfile;
}

export const HealthTab: React.FC<HealthTabProps> = ({ profile }) => {
  const { healthData } = profile;

  if (!healthData) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground text-center">No health data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Medications Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Pill className="h-4 w-4 text-primary" />
              Medications ({healthData.medications?.count || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.medications?.count > 0 ? (
              <>
                {healthData.medications.highRiskCount > 0 && (
                  <div className="mb-3 p-2 rounded bg-destructive/10 border border-destructive/20 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span className="text-sm text-destructive font-medium">
                      {healthData.medications.highRiskCount} high-risk medication(s)
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  {healthData.medications.list?.map((med, idx) => (
                    <div key={idx} className="p-2 rounded bg-muted/50 border border-border/50 flex justify-between items-center">
                      <div>
                        <span className="font-medium text-sm">{med.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{med.dosage}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{med.frequency}</span>
                        {med.isHighRisk && (
                          <Badge variant="destructive" className="text-xs">High Risk</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No medications recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Medication Adherence */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-green-500" />
              Medication Adherence (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.medicationLogs?.total30Days > 0 ? (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Adherence Rate</span>
                    <span className="text-sm font-medium">{healthData.medicationLogs.adherenceRate}%</span>
                  </div>
                  <Progress value={healthData.medicationLogs.adherenceRate} className="h-2" />
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 rounded bg-muted/50">
                    <p className="text-lg font-bold">{healthData.medicationLogs.total30Days}</p>
                    <p className="text-xs text-muted-foreground">Total Doses</p>
                  </div>
                  <div className="p-2 rounded bg-green-500/10">
                    <p className="text-lg font-bold text-green-600">{healthData.medicationLogs.takenCount}</p>
                    <p className="text-xs text-muted-foreground">Taken</p>
                  </div>
                  <div className="p-2 rounded bg-destructive/10">
                    <p className="text-lg font-bold text-destructive">{healthData.medicationLogs.missedCount}</p>
                    <p className="text-xs text-muted-foreground">Missed</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No medication logs in last 30 days</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Symptoms and Vitals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Symptoms */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Heart className="h-4 w-4 text-red-500" />
              Recent Symptoms ({healthData.symptomLogs?.recentCount || 0} in 30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.symptomLogs?.recent?.length > 0 ? (
              <div className="space-y-2">
                {healthData.symptomLogs.recent.map((symptom, idx) => (
                  <div key={idx} className="p-2 rounded bg-muted/50 border border-border/50 flex justify-between items-center">
                    <span className="text-sm">{symptom.symptom}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        symptom.severity === 'severe' ? 'destructive' :
                        symptom.severity === 'moderate' ? 'secondary' : 'outline'
                      } className="text-xs">
                        {symptom.severity}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {symptom.loggedAt ? format(new Date(symptom.loggedAt), 'MMM d') : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent symptoms logged</p>
            )}
          </CardContent>
        </Card>

        {/* Vital Signs */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-500" />
              Vital Signs ({healthData.vitalSigns?.totalRecorded || 0} recorded)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.vitalSigns?.recent?.length > 0 ? (
              <div className="space-y-2">
                {healthData.vitalSigns.recent.map((vital, idx) => (
                  <div key={idx} className="p-2 rounded bg-muted/50 border border-border/50 flex justify-between items-center">
                    <span className="text-sm capitalize">{vital.type?.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{vital.value} {vital.unit}</span>
                      <span className="text-xs text-muted-foreground">
                        {vital.recordedAt ? format(new Date(vital.recordedAt), 'MMM d') : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No vital signs recorded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Wellness Logs Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Wellness Logs (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Mood */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Smile className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium">Mood</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Entries</span>
                  <span className="font-medium">{healthData.wellnessLogs?.mood?.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Avg Rating</span>
                  <span className="font-medium">{healthData.wellnessLogs?.mood?.avgRating || '-'}/5</span>
                </div>
              </div>
            </div>

            {/* Sleep */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="h-4 w-4 text-indigo-500" />
                <span className="text-sm font-medium">Sleep</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Entries</span>
                  <span className="font-medium">{healthData.wellnessLogs?.sleep?.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Avg Hours</span>
                  <span className="font-medium">{healthData.wellnessLogs?.sleep?.avgHours || '-'}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Avg Quality</span>
                  <span className="font-medium">{healthData.wellnessLogs?.sleep?.avgQuality || '-'}/5</span>
                </div>
              </div>
            </div>

            {/* Nutrition */}
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Utensils className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium">Nutrition</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Entries</span>
                  <span className="font-medium">{healthData.wellnessLogs?.nutrition?.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Avg Meals/Day</span>
                  <span className="font-medium">{healthData.wellnessLogs?.nutrition?.avgMealsPerDay || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
